/**
 * Activity Tracker Context
 * Tracks ALL admin/employee activity for audit and performance metrics.
 * - All users: action tracking, page view tracking, heartbeat
 * - 30-minute idle period detection (logged to audit_idle_periods for payroll review)
 * - Session lock (PIN re-auth) is handled separately in AuthContext at 1 hour
 */
import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import IdleReasonModal from '../components/IdleReasonModal';
import { presenceSuppressed, callActiveSuppressed, acwOverageSince, acwStartedSince, clearAcwOverage } from '../constants/presence';
import { isEmployee } from '../constants/roles';

const ActivityTrackerContext = createContext(null);

const HEARTBEAT_INTERVAL = 60000;      // 60s
const IDLE_CHECK_INTERVAL = 30000;     // 30s
const IDLE_PERIOD_THRESHOLD = 20 * 60 * 1000;      // 20 min — flag as idle period for payroll tracking

// Phase 3.1a — per-USER activity across duplicate tabs (reps compare accounts in tabs).
const SHARED_ACTIVITY_KEY = 'tc_last_meaningful';   // localStorage: last-activity epoch shared by all tabs
const LEADER_LOCK = 'credlocity-timeclock-leader';  // Web Lock: exactly one tab owns idle detection
const ACTIVITY_CHANNEL = 'credlocity-activity';     // BroadcastChannel: keep the idle modal in sync
// Phase 3.1c — while a training is in progress in ANY tab, the training viewer writes a short,
// self-expiring epoch here; the (possibly different) leader tab reads it and suppresses the idle
// popup, because training time is paid work. Self-heals if the tab closes/crashes.
const TRAINING_ACTIVE_KEY = 'tc_training_active';

// Human-readable labels for common API mutations
const describeApiCall = (verb, url) => {
  const path = url.split('?')[0];
  const checks = [
    [/POST.*\/collections\/accounts$/, 'Added a new collections account'],
    [/PUT.*\/collections\/accounts\/[^/]+$/, 'Updated a collections account'],
    [/POST.*\/collections\/accounts\/.*\/call/, 'Logged a call on an account'],
    [/POST.*\/collections\/contacts/, 'Added a contact'],
    [/POST.*\/clients$/, 'Added a new client'],
    [/PUT.*\/clients\//, 'Updated client record'],
    [/POST.*\/clients\/.*\/notes/, 'Added a client note'],
    [/POST.*\/payroll\/commissions/, 'Logged a commission'],
    [/POST.*\/payroll\/bonuses/, 'Added a bonus'],
    [/POST.*\/activity\/timeclock\/clockin/, 'Clocked in'],
    [/POST.*\/activity\/timeclock\/clockout/, 'Clocked out'],
    [/POST.*\/outsourcing\/admin\/employees/, 'Added an outsourcing employee'],
    [/PUT.*\/outsourcing\/admin\/employees\//, 'Updated employee record'],
    [/POST.*\/blog/, 'Created blog content'],
    [/PUT.*\/blog\//, 'Updated blog content'],
    [/DELETE.*\//, 'Deleted a record'],
    [/POST.*\//, `${verb} action`],
    [/PUT.*\//, `${verb} update`],
  ];
  for (const [pattern, label] of checks) {
    if (pattern.test(`${verb} ${path}`)) return label;
  }
  return `${verb} ${path}`;
};

export const ActivityTrackerProvider = ({ children }) => {
  const { user, isAuthenticated, sessionLocked, pendingIdlePeriod, clearPendingIdlePeriod, logout, isImpersonating } = useAuth();
  const location = useLocation();

  const sessionIdRef = useRef(null);
  const lastMeaningfulRef = useRef(Date.now());
  const lastMeaningfulDetailRef = useRef('session_start');
  const lastPageRef = useRef('');
  const pageEnterRef = useRef(Date.now());
  const heartbeatRef = useRef(null);
  const idleCheckRef = useRef(null);
  const apiInterceptedRef = useRef(false);

  // Idle period state
  const idlePeriodOpenRef = useRef(false);
  const idlePeriodIdRef = useRef(null);
  const idleSinceRef = useRef(null);
  const idleRepromptRef = useRef(null);  // interval handle for the 10-min re-prompt
  // Holds the in-flight /idle-period/start Promise so handleIdleModalSubmit
  // can await it instead of firing a duplicate /start call on a slow Render response.
  const startPromiseRef = useRef(null);

  const [isTracking, setIsTracking] = useState(false);
  const [showIdleModal, setShowIdleModal] = useState(false);

  // Ref mirror of sessionLocked so closures in effects read current value without re-registering
  const sessionLockedRef = useRef(false);
  const prevSessionLockedRef = useRef(false);

  // Phase 3.1a — cross-tab per-user activity
  const isLeaderRef = useRef(false);        // this tab is the elected idle-detection leader
  const leaderReleaseRef = useRef(null);    // resolver that releases the Web Lock
  const bcRef = useRef(null);               // BroadcastChannel for idle-modal sync

  const getSessionId = useCallback(() => {
    if (sessionIdRef.current) return sessionIdRef.current;
    const stored = sessionStorage.getItem('audit_session_id');
    if (stored) { sessionIdRef.current = stored; return stored; }
    const sid = user?.session_id || `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionIdRef.current = sid;
    sessionStorage.setItem('audit_session_id', sid);
    return sid;
  }, [user]);

  // Record this tab's activity into the localStorage key shared by every tab of this user.
  const bumpShared = useCallback(() => {
    try { localStorage.setItem(SHARED_ACTIVITY_KEY, String(Date.now())); } catch { /* ignore */ }
  }, []);

  // Most-recent meaningful activity across ALL of this user's tabs. The user is ACTIVE if
  // ANY tab is active, so idle is always measured from this max — never one tab alone.
  const sharedLastActive = useCallback(() => {
    let shared = 0;
    try { shared = Number(localStorage.getItem(SHARED_ACTIVITY_KEY)) || 0; } catch { /* ignore */ }
    return Math.max(lastMeaningfulRef.current, shared);
  }, []);

  // True while a training is genuinely in progress in any of this user's tabs (see
  // TRAINING_ACTIVE_KEY). The idle detector reads this to suppress the popup mid-module.
  const trainingActive = useCallback(() => {
    try { return Date.now() < (Number(localStorage.getItem(TRAINING_ACTIVE_KEY)) || 0); }
    catch { return false; }
  }, []);

  const broadcast = useCallback((type, extra = {}) => {
    try { bcRef.current?.postMessage({ type, ...extra }); } catch { /* ignore */ }
  }, []);

  // ---- Log action (callable from anywhere) ----
  const recordAction = useCallback((category, detail, metadata = {}) => {
    if (!user) return;
    // Do not reset the idle clock while an idle period is open — the employee
    // must submit a reason before anything resets their activity timestamp.
    if (idlePeriodOpenRef.current) return;
    lastMeaningfulRef.current = Date.now();
    lastMeaningfulDetailRef.current = detail;
    bumpShared();
    api.post('/audit/log', {
      event_type: 'action',
      action_category: category,
      action_detail: detail,
      page: window.location.pathname,
      session_id: getSessionId(),
      metadata
    }).catch(() => {});
  }, [user, getSessionId, bumpShared]);

  // ---- Open an idle period — fires backend start IMMEDIATELY, then shows modal ----
  const openIdlePeriod = useCallback(async (sinceOverrideMs) => {
    if (idlePeriodOpenRef.current || !user || user?.is_exempt_from_timeclock) return;
    idlePeriodOpenRef.current = true;
    // sinceOverrideMs lets the After-Call-Work overage open its one idle period starting at the exact
    // limit moment (not from lastMeaningful) — still the SAME single writer, no second idle path.
    const idleSince = new Date(sinceOverrideMs || lastMeaningfulRef.current).toISOString();
    idleSinceRef.current = idleSince;
    setShowIdleModal(true);

    // Record start on backend right away so the period is captured even if user never submits the modal.
    // Store the Promise in startPromiseRef so handleIdleModalSubmit can await it on a slow response
    // rather than firing a duplicate /start call.
    const token = localStorage.getItem('auth_token');
    const sid = sessionIdRef.current || sessionStorage.getItem('audit_session_id');
    // When this is an After-Call-Work overage (sinceOverrideMs set), send the ACW START so the SERVER
    // re-derives the overage mark from the owner's acw_limit_minutes and clamps idle_start to it — the
    // server never trusts the client's claimed deadline (R2 idle clamp).
    const acwStart = sinceOverrideMs ? acwStartedSince() : null;
    const startBody = { session_id: sid, idle_since: idleSince };
    if (acwStart) startBody.acw_started_at = new Date(acwStart).toISOString();
    startPromiseRef.current = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/idle-period/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(startBody),
    })
      .then(r => r.json())
      .then(d => {
        if (d?.id) {
          idlePeriodIdRef.current = d.id;
          // Mirror this ONE period to sibling tabs so the employee can answer it from
          // whichever tab they're looking at; answering in any tab clears it everywhere.
          broadcast('idle_open', { period_id: d.id, idle_since: idleSince });
        }
        return d?.id || null;
      })
      .catch(() => null);

    await startPromiseRef.current;
  }, [user, broadcast]);

  // ---- R1.5: After-Call Work overage → the reason-required popup, for EVERYONE ----
  // Fires at the owner After-Call-Work limit REGARDLESS of mouse/keyboard activity. For a non-exempt rep
  // it opens ONE idle period through the existing single writer (openIdlePeriod), idle_since = the limit
  // moment. For an exempt owner it shows the SAME reason-required popup but writes NO backend idle period
  // (standing rule: exempt accounts never generate idle periods — Joey's ruling this run). Either way the
  // modal cannot be dismissed without a reason, and handleIdleModalSubmit closes cleanly when there is no
  // period id to attach.
  const fireAcwOverage = useCallback((sinceMs) => {
    if (idlePeriodOpenRef.current || !user) return;
    if (user?.is_exempt_from_timeclock) {
      idlePeriodOpenRef.current = true;                // lock so the popup can't be dismissed w/o a reason
      idleSinceRef.current = new Date(sinceMs || Date.now()).toISOString();
      setShowIdleModal(true);
    } else {
      openIdlePeriod(sinceMs);                          // the existing single idle writer
    }
  }, [user, openIdlePeriod]);

  const handleIdleModalSubmit = useCallback(async ({ reason, sub_category, client_names, meeting_type, explanation, screenshot, screenshot_name }) => {
    setShowIdleModal(false);
    idlePeriodOpenRef.current = false;
    // FIX 1: reset idle clock so the 30s check doesn't immediately re-fire openIdlePeriod
    lastMeaningfulRef.current = Date.now();
    bumpShared();
    sessionStorage.setItem('idle_reason_submitted', 'true');
    const token = localStorage.getItem('auth_token');
    const sid = sessionIdRef.current || sessionStorage.getItem('audit_session_id');
    const periodId = idlePeriodIdRef.current;

    const reasonPayload = { reason, sub_category, client_names, meeting_type, explanation, screenshot, screenshot_name };

    if (periodId) {
      // Period already recorded on backend — attach the reason then close
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/idle-period/${periodId}/reason`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(reasonPayload),
        });
      } catch (_) {}
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/idle-period/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sid, period_id: periodId }),
      }).catch(() => {});
    } else {
      // FIX 2: /start is still in-flight — await the stored Promise instead of firing a
      // duplicate /start call. Once it resolves we have the period id and can use the
      // normal attach-reason + close path. If /start failed entirely, resolvedId is null
      // and we do nothing (there's no open record to close).
      const resolvedId = startPromiseRef.current ? await startPromiseRef.current : null;
      if (resolvedId) {
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/idle-period/${resolvedId}/reason`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(reasonPayload),
          });
        } catch (_) {}
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/idle-period/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ session_id: sid, period_id: resolvedId }),
        }).catch(() => {});
      }
    }

    idlePeriodIdRef.current = null;
    idleSinceRef.current = null;
    startPromiseRef.current = null;
    broadcast('idle_cleared');
  }, [bumpShared, broadcast]);

  // ---- Close an idle period without a reason (logout path only) ----
  // Normal close path is handleIdleModalSubmit — that is the only way the employee
  // can clear the modal. This function is reserved for the logout recorder so that
  // an admin force-logout or session expiry can still proceed even with the modal open.
  const closeIdlePeriod = useCallback(() => {
    if (!idlePeriodOpenRef.current || !user) return;
    idlePeriodOpenRef.current = false;
    setShowIdleModal(false);
    const token = localStorage.getItem('auth_token');
    const sid = sessionIdRef.current || sessionStorage.getItem('audit_session_id');
    const periodId = idlePeriodIdRef.current;
    if (periodId) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/idle-period/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sid, period_id: periodId }),
      }).catch(() => {});
    }
    idlePeriodIdRef.current = null;
    idleSinceRef.current = null;
    broadcast('idle_cleared');
  }, [user, broadcast]);


  // ---- Session start ----
  // Employee-facing tracking (idle popup, heartbeat, activity/audit logging, page-view
  // tracking, exit dispositions) is gated on employee IDENTITY, not on token presence or
  // route. School students never populate `user`; a guest_teacher is a client. isTracking
  // stays false for any non-employee, which disables every effect below that keys off it —
  // so a future route can never re-leak the idle popup to a client. See constants/roles.js.
  useEffect(() => {
    // J4 (2026-09-28) — an impersonation session NEVER tracks: isTracking is the master switch every
    // activity effect below gates on (session start, work pings, idle detection, exit-disposition, the
    // API interceptor), so leaving it false stops the whole engine for an impersonated session. Nothing
    // is written toward the target's hours/activity.
    if (isAuthenticated && user && isEmployee(user) && !isImpersonating) {
      if (user.session_id) {
        sessionIdRef.current = user.session_id;
        sessionStorage.setItem('audit_session_id', user.session_id);
      }
      setIsTracking(true);
    } else {
      setIsTracking(false);
    }
  }, [isAuthenticated, user, isImpersonating]);

  // ---- Leader-tab election (Phase 3.1a): exactly one tab per user owns idle detection ----
  // Web Locks gives automatic failover: when the leader tab closes, its exclusive lock is
  // released and a waiting tab's callback runs, making it the new leader — so closing a
  // duplicate tab changes nothing. Auto-clockout stays disabled (Phase 0); if it ever
  // returns it must fire only on LAST-tab release — i.e. when this lock is finally released
  // with no tab left to reacquire it — never on a per-tab close.
  useEffect(() => {
    if (!user || !isTracking) return;
    let cancelled = false;
    if (navigator?.locks?.request) {
      navigator.locks.request(LEADER_LOCK, { mode: 'exclusive' }, () => new Promise((release) => {
        if (cancelled) { release(); return; }
        isLeaderRef.current = true;
        leaderReleaseRef.current = release;
      })).catch(() => { isLeaderRef.current = true; });
    } else {
      // No Web Locks support — every tab self-leads. Duplicate periods still cannot form:
      // the backend keys idle periods per USER (not per session) and the shared activity
      // timestamp prevents false idle; BroadcastChannel keeps the modal in sync.
      isLeaderRef.current = true;
    }
    return () => {
      cancelled = true;
      isLeaderRef.current = false;
      if (leaderReleaseRef.current) { leaderReleaseRef.current(); leaderReleaseRef.current = null; }
    };
  }, [user, isTracking]);

  // ---- Cross-tab idle-modal sync (Phase 3.1a) ----
  useEffect(() => {
    if (!user || !isTracking) return;   // employees only — isTracking is employee-gated
    const bc = ('BroadcastChannel' in window) ? new BroadcastChannel(ACTIVITY_CHANNEL) : null;
    bcRef.current = bc;
    if (bc) {
      bc.onmessage = (ev) => {
        const m = ev.data || {};
        if (m.type === 'idle_open') {
          // The leader tab opened the ONE idle period — mirror the modal here so the
          // employee can answer from whichever tab they are looking at.
          if (idlePeriodOpenRef.current) return;
          idlePeriodOpenRef.current = true;
          if (m.period_id) idlePeriodIdRef.current = m.period_id;
          idleSinceRef.current = m.idle_since || idleSinceRef.current;
          setShowIdleModal(true);
        } else if (m.type === 'idle_cleared') {
          // Answered (or logged out) in another tab — clear it everywhere.
          idlePeriodOpenRef.current = false;
          idlePeriodIdRef.current = null;
          idleSinceRef.current = null;
          setShowIdleModal(false);
          lastMeaningfulRef.current = Date.now();
        }
      };
    }
    return () => { try { bc?.close(); } catch { /* ignore */ } bcRef.current = null; };
  }, [user, isTracking]);

  // ---- Sync sessionLocked into ref; resurface idle modal the moment PIN lock clears ----
  useEffect(() => {
    const wasLocked = prevSessionLockedRef.current;
    sessionLockedRef.current = sessionLocked;
    prevSessionLockedRef.current = sessionLocked;
    if (wasLocked && !sessionLocked && idlePeriodOpenRef.current) {
      // User just unlocked — idle period was pending the whole time, show modal now
      setShowIdleModal(true);
      lastMeaningfulRef.current = Date.now(); // start counting idle from now
    }
  }, [sessionLocked]);

  // ---- Surface idle modal for any period that was open when the previous session ended ----
  useEffect(() => {
    if (!pendingIdlePeriod || !user || !isTracking) return;  // employees only
    idlePeriodOpenRef.current = true;
    idlePeriodIdRef.current = pendingIdlePeriod.id;
    idleSinceRef.current = pendingIdlePeriod.idle_start;
    setShowIdleModal(true);
    clearPendingIdlePeriod();
  }, [pendingIdlePeriod, user, isTracking, clearPendingIdlePeriod]);

  // ---- Page view tracking (all users) ----
  useEffect(() => {
    if (!user || !isTracking) return;
    const currentPage = location.pathname;
    // Skip the audit log and idle-clock reset while a period is open; still update
    // the bookkeeping refs so post-submit page durations are calculated correctly.
    if (lastPageRef.current && lastPageRef.current !== currentPage && !idlePeriodOpenRef.current) {
      const dur = Math.round((Date.now() - pageEnterRef.current) / 1000);
      api.post('/audit/log', {
        event_type: 'page_view',
        action_category: 'navigation',
        action_detail: `Viewed ${lastPageRef.current} for ${dur}s`,
        page: lastPageRef.current,
        session_id: getSessionId(),
        metadata: { duration_seconds: dur, from_page: lastPageRef.current, to_page: currentPage }
      }).catch(() => {});
      lastMeaningfulRef.current = Date.now();
      lastMeaningfulDetailRef.current = `Navigated to ${currentPage}`;
      bumpShared();
    }
    lastPageRef.current = currentPage;
    pageEnterRef.current = Date.now();
  }, [location.pathname, user, isTracking, getSessionId, bumpShared]);

  // ---- Intercept API mutations as meaningful actions (all users) ----
  useEffect(() => {
    if (!user || !isTracking || apiInterceptedRef.current) return;
    apiInterceptedRef.current = true;

    const origPost = api.post.bind(api);
    const origPut = api.put.bind(api);
    const origDelete = api.delete.bind(api);

    const wrap = (method, verb) => function (url, ...args) {
      // While an idle period is open, pass API calls through untouched — do not
      // reset the idle clock and do not attempt to close the modal. The employee
      // must submit a reason before any activity is recorded against their clock.
      if (
        !idlePeriodOpenRef.current &&
        !url.includes('/audit/') && !url.includes('/heartbeat') && !url.includes('/auth/refresh') &&
        !url.includes('/training/ping') && !url.includes('/dispositions/ping')
      ) {
        const label = describeApiCall(verb, url);
        lastMeaningfulRef.current = Date.now();
        lastMeaningfulDetailRef.current = label;
        bumpShared();
        // Log the action to audit log
        const token = localStorage.getItem('auth_token');
        const sid = sessionIdRef.current || sessionStorage.getItem('audit_session_id');
        if (token && sid) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/log`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              event_type: 'action',
              action_category: verb === 'DELETE' ? 'delete' : verb === 'POST' ? 'create' : 'update',
              action_detail: label,
              page: window.location.pathname,
              session_id: sid,
              metadata: { method: verb, url }
            })
          }).catch(() => {});
        }
      }
      return method(url, ...args);
    };

    api.post = wrap(origPost, 'POST');
    api.put = wrap(origPut, 'PUT');
    api.delete = wrap(origDelete, 'DELETE');

    return () => {
      api.post = origPost;
      api.put = origPut;
      api.delete = origDelete;
      apiInterceptedRef.current = false;
    };
  }, [user, isTracking, bumpShared]);

  // ---- Heartbeat (all users) ----
  useEffect(() => {
    if (!user || !isTracking) return;

    const beat = async () => {
      try {
        await api.post('/audit/heartbeat', {
          session_id: getSessionId(),
          current_page: window.location.pathname,
          // Report the shared cross-tab activity so this tab counts the person as active
          // whenever ANY of their tabs is active (server dedupes to one live-agent row).
          last_meaningful_action_time: new Date(sharedLastActive()).toISOString(),
          last_meaningful_action_detail: lastMeaningfulDetailRef.current,
        });
      } catch { /* ignore */ }

      // Check if an admin has force-logged out this user.
      // Use direct fetch() — NOT the axios instance — so a transient 401 (Render cold
      // start, token about to expire) does NOT trigger the axios 401 interceptor which
      // would do window.location.href = '/admin/login' and cause a hard page reload.
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (meRes.ok) {
            const data = await meRes.json();
            if (data?.force_logout_pending) {
              toast.error('You have been logged out by an administrator.');
              await logout();
            }
          }
          // If 401 or any error — silently ignore, do NOT redirect
        }
      } catch { /* ignore */ }
    };

    beat();
    heartbeatRef.current = setInterval(beat, HEARTBEAT_INTERVAL);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [user, isTracking, getSessionId, logout, sharedLastActive]);

  // ---- Idle period check (non-exempt users only, every 30s) — payroll tracking only ----
  useEffect(() => {
    if (!user || !isTracking || user?.is_exempt_from_timeclock) return;

    const check = () => {
      if (sessionLockedRef.current) return; // skip while PIN lock is showing

      // Idle is measured from the most recent activity across ALL of this user's tabs, and
      // only the elected leader tab may open a period — so duplicate tabs can never start an
      // idle period while any tab is active, and the popup is raised once (by the leader).
      // (The After-Call Work overage is handled separately below in its own NON-exempt-gated watcher,
      // so an exempt owner still sees that popup; this 20-min idle check stays exempt-gated.)
      const idleMs = Date.now() - sharedLastActive();
      // Suppress mid-training (paid time): a timed/active training in ANY tab holds trainingActive().
      // Suppress while a non-Ready presence status is declared (meeting/break/bathroom) — the declared
      // status IS the answer; presenceSuppressed() is the self-expiring cross-tab epoch (3.1c pattern).
      // Suppress On a Call + the first N minutes of After-Call Work — working time, never idle
      // (callActiveSuppressed() is the call lifecycle's self-expiring epoch, same pattern).
      if (idleMs >= IDLE_PERIOD_THRESHOLD && !idlePeriodOpenRef.current && isLeaderRef.current
          && !trainingActive() && !presenceSuppressed() && !callActiveSuppressed()) {
        openIdlePeriod();
      }

      // No safety-net close here. Once the modal is open the ONLY close path is a
      // successful Submit — check() must never auto-dismiss it.
    };

    idleCheckRef.current = setInterval(check, IDLE_CHECK_INTERVAL);
    return () => { if (idleCheckRef.current) clearInterval(idleCheckRef.current); };
  }, [user, isTracking, openIdlePeriod, sharedLastActive, trainingActive]);

  // ---- R1.5: After-Call Work overage watcher (every 5s) — runs for ALL employees ----
  // Deliberately NOT gated on is_exempt_from_timeclock (unlike the 20-min idle check above), so an exempt
  // owner still gets the reason-required popup at the limit. Reads the persisted overage DEADLINE from
  // localStorage (so it survives route changes AND a page refresh), fires exactly once at/after the
  // deadline through the single fireAcwOverage path, and clears the marker immediately — so it can never
  // fire twice, and the 20-min detector (which also checks !idlePeriodOpenRef.current) can never
  // double-record with it. If the rep clicks Ready before the limit, finishCall clears the marker first.
  useEffect(() => {
    if (!user || !isTracking) return undefined;
    const check = () => {
      if (sessionLockedRef.current) return;
      const deadline = acwOverageSince();
      if (deadline && Date.now() >= deadline && !idlePeriodOpenRef.current && isLeaderRef.current) {
        clearAcwOverage();
        fireAcwOverage(deadline);
      }
    };
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [user, isTracking, fireAcwOverage]);

  // ---- P4: 10-minute re-prompt — while the employee REMAINS idle, resurface the modal every 10 min ----
  // Fires only when idlePeriodOpenRef.current is true (period is open + modal was dismissed or
  // unanswered). Clears automatically when the period closes (handleIdleModalSubmit sets
  // idlePeriodOpenRef.current = false before this interval can fire again). This never starts a new
  // /idle-period/start call — it only re-shows the already-open modal so the employee is prompted
  // to submit a reason they may have dismissed.
  const IDLE_REPROMPT_INTERVAL = 10 * 60 * 1000; // 10 minutes
  useEffect(() => {
    if (!user || !isTracking || user?.is_exempt_from_timeclock) return;
    const reprompt = () => {
      // Only re-show if the period is still open AND the modal is not already visible AND the
      // session is not PIN-locked (PIN lock has its own resurface path).
      if (idlePeriodOpenRef.current && !sessionLockedRef.current) {
        setShowIdleModal(true);
      }
    };
    idleRepromptRef.current = setInterval(reprompt, IDLE_REPROMPT_INTERVAL);
    return () => { if (idleRepromptRef.current) clearInterval(idleRepromptRef.current); };
  }, [user, isTracking]);

  // ---- Track meaningful keyboard/click (ALL users) ----
  useEffect(() => {
    if (!user || !isTracking) return;

    const markActive = (detail) => {
      if (sessionLockedRef.current) return; // PIN entry must not reset idle tracking
      lastMeaningfulRef.current = Date.now();
      if (detail) lastMeaningfulDetailRef.current = detail;
      bumpShared();
    };

    // Tags that are purely for reading — clicking them should NOT reset the idle clock
    // even if they inherit cursor:pointer from a clickable ancestor.
    const PASSIVE_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'MAIN', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'ASIDE']);

    const onClick = (e) => {
      // While an idle period is open, ALL clicks are ignored by the generic activity
      // path. Submit is the only action that matters at that point, and it is handled
      // directly by handleIdleModalSubmit — not through this listener.
      if (idlePeriodOpenRef.current) return;

      const t = e.target;
      const isSemanticInteractive = (
        t.tagName === 'BUTTON' || t.tagName === 'A' || t.tagName === 'INPUT' ||
        t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' ||
        t.closest('button') || t.closest('a') || t.dataset?.testid
      );
      // cursor:pointer is the browser's own signal that an element is clickable —
      // catches div-rows, tabs, cards, and other non-semantic interactive elements
      // without requiring ARIA roles. Passive structural tags are excluded to avoid
      // counting clicks on static text that inherited pointer from a parent container.
      const isClickable = isSemanticInteractive ||
        (!PASSIVE_TAGS.has(t.tagName) && window.getComputedStyle(t).cursor === 'pointer');
      if (isClickable) {
        markActive(`Click: ${t.dataset?.testid || t.textContent?.slice(0, 30) || t.tagName}`);
      }
    };

    const onKey = (e) => {
      // Same lockdown as onClick — no keystrokes reset the idle clock while the modal is open.
      if (idlePeriodOpenRef.current) return;

      if (e.key.length === 1 || e.key === 'Enter' || e.key === 'Backspace' || e.key === 'Tab') {
        const t = e.target;
        if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) {
          markActive(`Typing in ${t.dataset?.testid || t.name || t.tagName}`);
        }
      }
    };

    // onMouseMove deliberately removed: moving the mouse toward the idle modal to
    // answer it was triggering closeIdlePeriod() and dismissing the modal before
    // the employee could select a reason. The idle period now only closes on a
    // genuine Submit via handleIdleModalSubmit.

    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [user, isTracking, bumpShared]);

  // ---- Expose logout recorder for AuthContext ----
  useEffect(() => {
    if (user) {
      window.__auditRecordLogout = async (reason = 'manual') => {
        // Close any open idle period on logout — this is the one permitted exception
        // to the "only Submit can close the modal" rule. An admin force-logout or
        // session expiry must not be blocked by an unanswerable modal.
        if (idlePeriodOpenRef.current) closeIdlePeriod();
        const sid = sessionIdRef.current || sessionStorage.getItem('audit_session_id');
        if (sid) {
          try {
            await api.post('/audit/logout-event', { session_id: sid, reason });
          } catch { /* ignore */ }
        }
        sessionStorage.removeItem('audit_session_id');
        sessionIdRef.current = null;
      };
    }
    return () => { window.__auditRecordLogout = null; };
  }, [user, closeIdlePeriod]);

  const value = { isTracking, sessionId: sessionIdRef.current, recordAction };

  return (
    <ActivityTrackerContext.Provider value={value}>
      {children}
      {showIdleModal && <IdleReasonModal onSubmit={handleIdleModalSubmit} idleSince={idleSinceRef.current} />}
      {/* NAV B2 (Joey, Oct 13): the global "Log Activity" affordance moved OUT of this floating
          overlay and INTO the admin top bar (next to Clock Out) — see Dashboard.js. The floating
          button used to cover page content (the Business Overview card corner), so it was removed.
          The same DispositionButton (surface="global") now lives in the header; this provider keeps
          only the idle modal + activity context. Public routes never render it (the top bar is
          /admin-only). */}
    </ActivityTrackerContext.Provider>
  );
};

export const useActivityTracker = () => {
  const ctx = useContext(ActivityTrackerContext);
  if (!ctx) return { isTracking: false, sessionId: null, recordAction: () => {} };
  return ctx;
};

export default ActivityTrackerProvider;
